import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Users, Star, LogOut, Settings, BarChart3 } from 'lucide-react';
import { BACKEND_URL } from '../config/api.config';
import { useOrganizacion } from '../context/OrganizacionContext';

const DashboardUnificado = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [liderInfo, setLiderInfo] = useState(null);
  const [empleadosACargo, setEmpleadosACargo] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { config, logoUrl } = useOrganizacion();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const storedUserInfo = localStorage.getItem('user_info');

    if (!token || !storedUserInfo) {
      toast.error('Sesión no válida. Por favor inicie sesión nuevamente.');
      navigate('/');
      return;
    }

    const user = JSON.parse(storedUserInfo);
    setUserInfo(user);
    
    // Cargar información según el rol
    if (user.es_lider) {
      setEmpleadosACargo(user.empleados_a_cargo || []);
    }
    
    if (user.tiene_lider) {
      fetchLiderInfo(user.id, token);
    } else {
      setIsLoading(false);
    }
  }, [navigate]);

  const fetchLiderInfo = async (empleadoId, token) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/empleados-info/${empleadoId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al obtener información del líder');
      }

      const data = await response.json();
      
      if (data.lider) {
        setLiderInfo(data.lider);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEvaluarEmpleado = (empleado) => {
    const evaluacionData = {
      evaluador: {
        user_id: userInfo.id,
        user_name: userInfo.nombre,
        user_type: 'lider',
        cargo: userInfo.cargo || 'Líder'
      },
      evaluado: {
        id: empleado.id,
        nombre: empleado.nombre_completo,
        cargo: empleado.cargo
      }
    };
    
    localStorage.setItem('evaluacionData', JSON.stringify(evaluacionData));
    navigate('/evaluacion-empleado-dinamica');
  };

  const handleEvaluarLider = () => {
    if (!liderInfo) {
      toast.error('No se encontró información del líder');
      return;
    }

    const evaluacionData = {
      evaluador: {
        user_id: userInfo.id,
        user_name: userInfo.nombre,
        user_type: 'empleado',
        cargo: 'Empleado'
      },
      evaluado: {
        id: liderInfo.id,
        nombre: liderInfo.nombre_completo,
        cargo: liderInfo.cargo || 'Líder'
      }
    };

    localStorage.setItem('evaluacionData', JSON.stringify(evaluacionData));
    navigate('/evaluacion-lider-dinamica');
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('evaluacionData');
    toast.success('Sesión cerrada exitosamente');
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando información...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {logoUrl && config.mostrar_logo && (
                <img
                  src={logoUrl}
                  alt={config.nombre_organizacion || 'Logo'}
                  className="h-12 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div>
                <h1 
                  className="text-2xl font-bold"
                  style={{ color: config.color_primario }}
                >
                  ¡Bienvenido, {userInfo?.nombre}!
                </h1>
                <p className="text-gray-600">
                  {userInfo?.es_lider_intermedio 
                    ? 'Líder de Equipo' 
                    : userInfo?.es_lider 
                    ? 'Líder' 
                    : 'Empleado'}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>

        {/* Sección de Evaluaciones */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📊 Evaluaciones Disponibles</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Evaluar Empleados (si es líder) */}
            {userInfo?.es_lider && empleadosACargo.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 rounded-full p-3 mr-4">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Evaluar Mi Equipo
                    </h3>
                    <p className="text-sm text-gray-600">
                      {empleadosACargo.length} empleado{empleadosACargo.length !== 1 ? 's' : ''} a tu cargo
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {empleadosACargo.map((empleado) => (
                    <div
                      key={empleado.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{empleado.nombre_completo}</p>
                        <p className="text-sm text-gray-600">{empleado.cargo}</p>
                      </div>
                      <button
                        onClick={() => handleEvaluarEmpleado(empleado)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        Evaluar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Evaluar Líder (si tiene líder) */}
            {userInfo?.tiene_lider && liderInfo && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-purple-100 rounded-full p-3 mr-4">
                    <Star className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Evaluación 360°
                    </h3>
                    <p className="text-sm text-gray-600">
                      Evalúa a tu líder directo
                    </p>
                  </div>
                </div>
                
                <div className="p-4 bg-purple-50 rounded-lg mb-4">
                  <p className="font-medium text-gray-900">{liderInfo.nombre_completo}</p>
                  <p className="text-sm text-gray-600">{liderInfo.cargo}</p>
                </div>
                
                <button
                  onClick={handleEvaluarLider}
                  className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  Evaluar a mi Líder
                </button>
              </div>
            )}

            {/* Mensaje si no hay evaluaciones disponibles */}
            {!userInfo?.es_lider && !userInfo?.tiene_lider && (
              <div className="col-span-2 bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <BarChart3 className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No hay evaluaciones disponibles
                </h3>
                <p className="text-gray-600">
                  No tienes evaluaciones pendientes en este momento.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Sección de Administración (solo para admins) */}
        {userInfo?.es_administrador && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">⚙️ Administración</h2>
            
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="bg-green-100 rounded-full p-3 mr-4">
                  <Settings className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Panel de Administración
                  </h3>
                  <p className="text-sm text-gray-600">
                    Gestiona el sistema de evaluaciones
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Ir al Panel de Administración
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardUnificado;
